import { useEffect, useRef, useState } from 'react'
import cx from 'classnames'

import { IconButton } from './IconButton'
import { Tooltip } from './Tooltip'

export const CopyButton = ({
  children,
  className,
  copiedIcon,
  copiedLabel = 'Copied',
  copyIcon,
  copyLabel = 'Copy',
  feedbackDuration = 3000,
  text,
  tone = 'default',
}) => {
  const [copied, setCopied] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [liveAnnouncement, setLiveAnnouncement] = useState('')
  const announcementTimeout = useRef(null)
  const resetTimeout = useRef(null)

  useEffect(() => () => {
    window.clearTimeout(announcementTimeout.current)
    window.clearTimeout(resetTimeout.current)
  }, [])

  const handleCopy = async () => {
    window.clearTimeout(resetTimeout.current)
    window.clearTimeout(announcementTimeout.current)
    resetTimeout.current = null
    announcementTimeout.current = null

    try {
      await navigator.clipboard.writeText(text)
    } catch (_error) {
      setCopied(false)
      setFeedbackOpen(false)
      setLiveAnnouncement('')
      return
    }

    setCopied(true)
    setFeedbackOpen(true)
    setLiveAnnouncement('')
    announcementTimeout.current = window.setTimeout(() => {
      setLiveAnnouncement(copiedLabel)
      announcementTimeout.current = null
    }, 0)

    resetTimeout.current = window.setTimeout(() => {
      setCopied(false)
      setFeedbackOpen(false)
      setLiveAnnouncement('')
      resetTimeout.current = null
    }, feedbackDuration)
  }

  const trigger = children ? (
    <button
      aria-label={`${copyLabel}: ${text}`}
      className={cx(
        'ds-copy-value ds-mono tw:inline-flex tw:items-center tw:rounded-button tw:border tw:px-3 tw:py-2 tw:text-left',
        'tw:transition-[background-color,border-color,box-shadow,color] tw:duration-fast tw:ease-standard tw:focus:outline-transparent tw:focus:shadow-focus-input',
        copied
          ? 'tw:border-status-success-border tw:bg-status-success-surface tw:text-status-success-foreground tw:hover:bg-status-success-surface'
          : tone === 'inverse'
            ? 'tw:border-hero-control-border tw:bg-hero-control-background tw:text-hero-control-foreground tw:hover:border-hero-control-border-hover tw:hover:bg-hero-control-background-hover'
            : 'tw:border-strong tw:bg-surface-muted tw:hover:bg-surface-subtle',
        className,
      )}
      data-state={copied ? 'copied' : 'idle'}
      onClick={handleCopy}
      type="button"
    >
      {children}
    </button>
  ) : (
    <IconButton
      className={className}
      data-state={copied ? 'copied' : 'idle'}
      label={copyLabel}
      onClick={handleCopy}
      variant={copied ? 'success' : 'ghost'}
    >
      {copied ? copiedIcon : copyIcon}
    </IconButton>
  )

  return (
    <>
      <Tooltip
        content={copied ? copiedLabel : copyLabel}
        onDismiss={() => setFeedbackOpen(false)}
        open={copied && feedbackOpen}
        tone={copied ? 'success' : 'default'}
      >
        {trigger}
      </Tooltip>
      <span aria-live="polite" className="tw:sr-only">{liveAnnouncement}</span>
    </>
  )
}
